import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { Moment } from 'moment';
import { BehaviorSubject, distinctUntilChanged, filter, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostCenter } from '../../../../models/cost-center.model';
import { IHourlyWage } from '../../../../models/hourly-wage-model';
import { ICostCenterState } from '../../../../services/facades/master-data/cost-center-state.model';
import { CostCenterFacade } from '../../../../services/facades/master-data/cost-center.facade';
import { isDatefilterValid, isOverlapping, stringToMoment } from '../../../../utils/moment-utils';
import { AddWageDialog } from './add-wage-dialog/add-wage-dialog.component';

@Component({
    templateUrl: './hourly-wage-dialog.component.html',
    styleUrls: [ './hourly-wage-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HourlyWageDialogComponent implements OnInit, AfterViewInit {

    @ViewChild(MatSort)
    public sort: MatSort;

    public costCenterState$: Observable<ICostCenterState>;
    public dataSource: MatTableDataSource<IHourlyWage> = new MatTableDataSource<IHourlyWage>([]);

    public filterFormGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);

    constructor(
        private myMatDialogRef: MatDialogRef<HourlyWageDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public costCenter: ICostCenter,
        private myCostCenterFacade: CostCenterFacade,
        private myMatDialog: MatDialog,
    ) {
    }

    public ngOnInit(): void {
        this.costCenterState$ = this.myCostCenterFacade.subState();
        this.costCenterState$
            .pipe(map((s: ICostCenterState) => s.hourlyWages))
            .subscribe((data: IHourlyWage[]) => {
                this.dataSource.data = data;
            });
        void this.myCostCenterFacade.fetchHourlyWages();
        this.dataSource.filterPredicate = (hourlyWage: IHourlyWage, _filterString: string): boolean => {
            const filterValues: { costAccountingType: number, validity: DateRange<Moment> } = this.filterFormGroup$.value.getRawValue();

            if (!isNullOrUndefined(filterValues.costAccountingType) && filterValues.costAccountingType !== hourlyWage.costAccountingType.id) {
                return false;
            }

            if (!isNullOrUndefined(filterValues.validity.start)
                && !isOverlapping(filterValues.validity, new DateRange<Moment>(stringToMoment(hourlyWage.validFrom), stringToMoment(hourlyWage.validTo)))) {
                return false;
            }

            return true;
        };
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    public onOpenAddDialog(): void {
        const dialog: MatDialogRef<AddWageDialog> = this.myMatDialog.open(AddWageDialog, {
            width: '600px',
            data: this.costCenter,
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostCenterFacade.fetchHourlyWages();
            }
        });
    }

    public onDeleteWage(wage: IHourlyWage): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            data: {
                message: 'app.dialog.hourly-wage.confirm-delete.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostCenterFacade.deleteHourlyWage(this.costCenter.id, wage.id);
                void this.myCostCenterFacade.fetchHourlyWages();
            }
        });
    }


    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public onFormGroupChange(formGroup: FormGroup): void {

        formGroup.valueChanges.pipe(
            map((formData: { costAccountingType: string | number, validity: DateRange<Moment | string> }) => {
                const costAccountingType: number = typeof formData.costAccountingType === 'string' ? undefined : formData.costAccountingType;
                const start: Moment = typeof formData.validity.start === 'string' ? undefined : formData.validity.start;
                const end: Moment = typeof formData.validity.end === 'string' ? undefined : formData.validity.end;
                if (formData.costAccountingType === '') {
                    formGroup.controls[ 'costAccountingType' ].patchValue(undefined, { emitEvent: false });
                }
                if (formData.validity.start === '') {
                    formGroup.controls[ 'validity' ].get('start').patchValue(undefined, { emitEvent: false });
                }
                if (formData.validity.end === '') {
                    formGroup.controls[ 'validity' ].get('end').patchValue(undefined, { emitEvent: false });
                }
                return {
                    costAccountingType,
                    validity: new DateRange<Moment>(start, end),
                };
            }),
            filter((formData: { costAccountingType: number, validity: DateRange<Moment> }) => isDatefilterValid(formData.validity.start, formData.validity.end)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        ).subscribe(() => {
            // Trigger the filterPredicate; The actual filterValue is not used
            this.dataSource.filter = 'dummyValue';
        });

        this.filterFormGroup$.next(formGroup);
    }
}
